import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { DemoGraphModal } from './DemographModal'
import { InsuranceModal } from './InsuranceModal'
import { relationshipOptions } from '../../../../containers/AppointmentBooking/constant'

function resolveRelationshipLabel(relationship) {
    if (!relationship) return null
    if (typeof relationship === 'object' && relationship.value) return relationship.value
    const match = relationshipOptions.find(opt => opt.key === relationship || opt.value === relationship)
    return match?.value || null
}


const font = (size, weight, color) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: size,
    fontWeight: weight,
    color,
})

const sectionCardSx = {
    border: '1px solid #E5EAF3',
    borderRadius: '12px',
    background: '#fff',
    overflow: 'hidden',
}

const noInsuranceCardSx = {
    ...sectionCardSx,
    minHeight: '122px',
}

const noInsuranceAddressCardSx = {
    ...sectionCardSx,
    minHeight: '82px',
}

const insuranceCardRowSx = {
    ...sectionCardSx,
    marginBottom: '10px',
}

const sectionHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderBottom: '1px solid #EAEFF7',
    background: '#fff',
}

const sectionBodySx = {
    padding: '10px 14px',
}

const compactSectionBodySx = {
    padding: '12px 14px',
}

const valueLabelSx = {
    ...font('13px', 400, '#667085'),
    marginRight: '6px',
}

const valueTextSx = {
    ...font('14px', 500, '#111827'),
}

const compactRowSx = {
    marginBottom: '7px',
}

const editBtnSx = {
    ...font('14px', 500, '#5571C6'),
    cursor: 'pointer',
}


function formatDob(dob) {
    if (!dob) return '-'
    return dob
}

function formatPhone(phone) {
    if (!phone) return '-'
    const cleaned = String(phone).replace(/\D/g, '')
    if (cleaned.length !== 10) return phone
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
}

function getFullName(data = {}) {
    return [data?.first_name, data?.last_name].filter(Boolean).join(' ') || '-'
}

function getFullAddress(data = {}) {
    const parts = [
        data?.address_one || data?.address1,
        data?.address_two || data?.address2,
        data?.city,
        data?.state,
        data?.zip_code || data?.zip,
    ].filter(Boolean)
    return parts.length ? parts.join(', ') : '-'
}

function displayName(name = '') {
    if (!name) return ''
    const lastDot = name.lastIndexOf('.')
    if (lastDot === -1) return name.length > 20 ? `${name.slice(0, 20)}...` : name
    const base = name.slice(0, lastDot)
    const ext = name.slice(lastDot + 1)
    return base.length > 20 ? `${base.slice(0, 20)}....${ext}` : name
}

const VerifyInformationStep = forwardRef(({
    patientData = {},
    insuranceData = {},
    carrierList = [],
    onUpdateInsurance,
    isMobileView,
    selctedProviderDetails,
    onBackToProvider,
    handleNextStep,
    selfpay = false,
    onModeChange,
    onInsuranceValidityChange,
}, ref) => {
    const amdPatientDetails = useSelector((state) => state?.patient?.amdPatientDetails)
    const patientCreateObject = useSelector((state) => state?.patient?.patientCreateObject)

    const demographicsRef = useRef(null)
    const insuranceRef = useRef(null)

    const [mode, setMode] = useState('summary')

    const changeMode = (newMode) => {
        setMode(newMode)
        onModeChange?.(newMode)
    }

    const patientSummary = useMemo(() => {
        return {
            ...amdPatientDetails,
            ...patientCreateObject,
            ...patientData,
        }
    }, [amdPatientDetails, patientCreateObject, patientData])

    const insuranceSummary = useMemo(() => {
        return {
            ...insuranceData,
        }
    }, [insuranceData])

    const resolvedCarrierName = useMemo(() => {
        if (insuranceSummary?.insurance_name) return insuranceSummary.insurance_name
        if (insuranceSummary?.carrier?.['@name']) return insuranceSummary.carrier['@name']
        if (insuranceSummary?.insurance_Id && carrierList.length > 0) {
            const match = carrierList.find(
                (c) => String(c?.['@id']) === String(insuranceSummary.insurance_Id)
            )
            if (match) return match['@name']
        }
        return null
    }, [insuranceSummary, carrierList])

    const subscriberId = insuranceSummary?.subscriber_id || insuranceSummary?.subscriberNum || null
    const resolvedRelationship = resolveRelationshipLabel(insuranceSummary?.relationship)

    const hasInsuranceInfo =
    !selfpay &&
    !!(
        resolvedCarrierName ||
        insuranceSummary?.insurance_Id ||
        insuranceSummary?.subscriber_id ||
        insuranceSummary?.subscriberNum ||
        insuranceSummary?.group_name ||
        insuranceSummary?.group_number ||
        insuranceSummary?.patientInsuranceFront?.name ||
        insuranceSummary?.patientInsuranceBack?.name
    )

    useImperativeHandle(ref, () => ({
        submit: () => {
            if (mode === 'summary') {
                handleNextStep?.()
                return
            }

            if (mode === 'editInsurance') {
                insuranceRef.current?.submit?.()
                return
            }

            demographicsRef.current?.submit?.()
        },
        isValid: true,
    }))

    const handleDemoSave = () => {
        changeMode('summary')
    }

    const handleInsuranceSave = () => {
        changeMode('summary')
    }

    if (mode === 'editPersonal' || mode === 'editAddress') {
        return (
            <DemoGraphModal
                ref={demographicsRef}
                isOpenModal={true}
                embedded={true}
                setSteps={() => changeMode('summary')}
                steps={3.5}
                handleNextStep={handleDemoSave}
                isMobileView={isMobileView}
                selctedProviderDetails={selctedProviderDetails}
                backToProvider={onBackToProvider}
            />
        )
    }

    if (mode === 'editInsurance') {
        return (
            <InsuranceModal
                ref={insuranceRef}
                isOpenModal={true}
                embedded={true}
                setSteps={() => changeMode('summary')}
                handleNextStep={handleInsuranceSave}
                carrierList={carrierList}
                insuranceData={insuranceSummary}
                onUpdateInsurance={onUpdateInsurance}
                onValidityChange={onInsuranceValidityChange}
            />
        )
    }

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: hasInsuranceInfo ? '14px 18px 8px 18px' : '22px 18px 12px 18px',
                background: '#fff',
            }}
        >
            <Typography sx={{ ...font('18px', 700, '#111827'), marginBottom: '4px' }}>
                Verify Your Information
            </Typography>

            <Typography
                sx={{
                    ...font('14px', 400, '#667085'),
                    marginBottom: hasInsuranceInfo ? '12px' : '10px',
                }}
            >
                Please review your details to make sure everything is correct.
            </Typography>

           {!hasInsuranceInfo ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                    }}
                >
                    <Box sx={noInsuranceCardSx}>
                        <Box sx={sectionHeaderSx}>
                            <Typography sx={{ ...font('15px', 700, '#111827') }}>
                                Personal Information
                            </Typography>
                            <Typography sx={editBtnSx} onClick={() => changeMode('editPersonal')}>
                                Edit
                            </Typography>
                        </Box>

                        <Box sx={compactSectionBodySx}>
                            <Typography
                                sx={{ ...font('15px', 600, '#111827'), marginBottom: '10px' }}
                            >
                                {getFullName(patientSummary)}
                            </Typography>

                            <Box sx={compactRowSx}>
                                <Typography component="span" sx={valueLabelSx}>
                                    Date of Birth:
                                </Typography>
                                <Typography component="span" sx={valueTextSx}>
                                    {formatDob(patientSummary?.dob)}
                                </Typography>
                            </Box>

                            <Box sx={compactRowSx}>
                                <Typography component="span" sx={valueLabelSx}>
                                    Phone Number:
                                </Typography>
                                <Typography component="span" sx={valueTextSx}>
                                    {formatPhone(patientSummary?.phone_number)}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography component="span" sx={valueLabelSx}>
                                    Email:
                                </Typography>
                                <Typography component="span" sx={valueTextSx}>
                                    {patientSummary?.email || '-'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={noInsuranceAddressCardSx}>
                        <Box sx={sectionHeaderSx}>
                            <Typography sx={{ ...font('15px', 700, '#111827') }}>
                                Address
                            </Typography>
                            <Typography sx={editBtnSx} onClick={() => changeMode('editAddress')}>
                                Edit
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                ...compactSectionBodySx,
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '46px',
                            }}
                        >
                            <Typography sx={{ ...font('14px', 500, '#111827'), lineHeight: 1.5 }}>
                                {getFullAddress(patientSummary)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            ) : (
                <>
                    <Box sx={insuranceCardRowSx}>
                        <Box sx={sectionHeaderSx}>
                            <Typography sx={{ ...font('15px', 700, '#111827') }}>
                                Personal Information
                            </Typography>
                            <Typography sx={editBtnSx} onClick={() => changeMode('editPersonal')}>
                                Edit
                            </Typography>
                        </Box>

                        <Box sx={sectionBodySx}>
                            <Typography
                                sx={{ ...font('15px', 600, '#111827'), marginBottom: '8px' }}
                            >
                                {getFullName(patientSummary)}
                            </Typography>

                            <Box sx={{ marginBottom: '6px' }}>
                                <Typography component="span" sx={valueLabelSx}>
                                    Date of Birth:
                                </Typography>
                                <Typography component="span" sx={valueTextSx}>
                                    {formatDob(patientSummary?.dob)}
                                </Typography>
                            </Box>

                            <Box sx={{ marginBottom: '6px' }}>
                                <Typography component="span" sx={valueLabelSx}>
                                    Phone Number:
                                </Typography>
                                <Typography component="span" sx={valueTextSx}>
                                    {formatPhone(patientSummary?.phone_number)}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography component="span" sx={valueLabelSx}>
                                    Email:
                                </Typography>
                                <Typography component="span" sx={valueTextSx}>
                                    {patientSummary?.email || '-'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={insuranceCardRowSx}>
                        <Box sx={sectionHeaderSx}>
                            <Typography sx={{ ...font('15px', 700, '#111827') }}>
                                Address
                            </Typography>
                            <Typography sx={editBtnSx} onClick={() => changeMode('editAddress')}>
                                Edit
                            </Typography>
                        </Box>

                        <Box sx={sectionBodySx}>
                            <Typography sx={{ ...font('14px', 500, '#111827') }}>
                                {getFullAddress(patientSummary)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={sectionCardSx}>
                        <Box sx={sectionHeaderSx}>
                            <Typography sx={{ ...font('15px', 700, '#111827') }}>
                                Insurance Information
                            </Typography>
                            <Typography sx={editBtnSx} onClick={() => changeMode('editInsurance')}>
                                Edit
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                ...sectionBodySx,
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: '8px',
                            }}
                        >
                            <Box
                                sx={{
                                    border: '1px solid #EAEFF7',
                                    borderRadius: '10px',
                                    padding: '10px 12px',
                                }}
                            >
                                <Box sx={{ marginBottom: '6px' }}>
                                    <Typography component="span" sx={valueLabelSx}>
                                        Insurance Name:
                                    </Typography>
                                    <Typography component="span" sx={valueTextSx}>
                                        {resolvedCarrierName || '-'}
                                    </Typography>
                                </Box>

                                <Box sx={{ marginBottom: '6px' }}>
                                    <Typography component="span" sx={valueLabelSx}>
                                        Subscriber ID:
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            ...valueTextSx,
                                            ...(!subscriberId && { color: '#D97706' }),
                                        }}
                                    >
                                        {subscriberId || '-'}
                                    </Typography>
                                </Box>

                                {subscriberId && (
                                    <Box sx={{ marginBottom: '6px' }}>
                                        <Typography component="span" sx={valueLabelSx}>
                                            Relationship to Insured:
                                        </Typography>
                                        <Typography component="span" sx={valueTextSx}>
                                            {resolvedRelationship || '-'}
                                        </Typography>
                                    </Box>
                                )}

                                {!subscriberId && (
                                    <Box sx={{ marginBottom: '6px' }}>
                                        <Typography sx={{ ...font('12px', 500, '#D97706') }}>
                                            Please edit insurance info to add Subscriber ID and Relationship to Insured.
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ marginBottom: '6px' }}>
                                    <Typography component="span" sx={valueLabelSx}>
                                        Group Name:
                                    </Typography>
                                    <Typography component="span" sx={valueTextSx}>
                                        {insuranceSummary?.group_name || '-'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography component="span" sx={valueLabelSx}>
                                        Group Number:
                                    </Typography>
                                    <Typography component="span" sx={valueTextSx}>
                                        {insuranceSummary?.group_number || '-'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    border: '1px solid #EAEFF7',
                                    borderRadius: '10px',
                                    padding: '10px 12px',
                                }}
                            >
                                <Typography
                                    sx={{ ...font('14px', 600, '#374151'), marginBottom: '10px' }}
                                >
                                    Insurance Card
                                </Typography>

                                <Box sx={{ marginBottom: '8px' }}>
                                    <Typography component="span" sx={valueLabelSx}>
                                        Front:
                                    </Typography>
                                    <Typography component="span" sx={{ ...font('14px', 500, '#5571C6') }}>
                                        {insuranceSummary?.patientInsuranceFront?.name
                                            ? displayName(insuranceSummary.patientInsuranceFront.name)
                                            : 'Upload'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography component="span" sx={valueLabelSx}>
                                        Back:
                                    </Typography>
                                    <Typography component="span" sx={{ ...font('14px', 500, '#5571C6') }}>
                                        {insuranceSummary?.patientInsuranceBack?.name
                                            ? displayName(insuranceSummary.patientInsuranceBack.name)
                                            : 'Upload'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )
})

export default VerifyInformationStep